import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CircleHelp, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { awaitRefresh } from "@/lib/awaitRefresh";
import { tenantKeys } from "@/hooks/useTenantQueries";
import { userKeys } from "@/hooks/useUserQueries";
import { adminAgreementService } from "@/services/admin-agreement.service";

const DESCRIPTION =
  "Acceptances given before 2026-10-19 are kept in each tenant's settings, which the commitment check no longer reads. This copies them to the tenant and to each person who accepted, so nobody who already agreed is asked again.";

/**
 * A card of the Helper page (SuperAdmin only): runs `POST /admin/agreements/backfill`, the step
 * the PR 68625 release needs once after the deploy — with both commitment flags off until it has
 * run. The legacy keys stay in settings, so a second run scans the same tenants and copies nothing
 * — except a commitment revoked since, which it copies back: the revoke clears only the columns.
 */
export function CopyAgreementAcceptancesCard() {
  const queryClient = useQueryClient();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const backfill = useMutation({
    mutationFn: () => adminAgreementService.backfill(),
    // The commitment status and filter on the tenant and user screens read the copied columns.
    onSettled: () =>
      awaitRefresh(
        Promise.all([
          queryClient.invalidateQueries({ queryKey: tenantKeys.all }),
          queryClient.invalidateQueries({ queryKey: userKeys.all }),
        ]),
      ),
  });

  const result = backfill.data;
  const copiedNothing =
    result !== undefined &&
    result.updatedTenantCount === 0 &&
    result.updatedUserCount === 0;

  return (
    <Card className="shadow-none">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 p-4 pb-3">
        <CardTitle className="text-base">Agreement acceptances</CardTitle>
        <HowThisWorks />
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="flex flex-wrap items-center gap-3 rounded-md border p-3 sm:flex-nowrap">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Legacy acceptances</p>
            <p className="text-sm text-muted-foreground">{DESCRIPTION}</p>
          </div>
          <div className="shrink-0 text-sm" aria-live="polite">
            {result !== undefined && !copiedNothing && (
              <Badge variant="outline" className="whitespace-nowrap">
                {result.updatedTenantCount} tenant(s), {result.updatedUserCount}{" "}
                user(s) updated of {result.scannedCount}
              </Badge>
            )}
            {copiedNothing && (
              <Badge variant="outline" className="whitespace-nowrap">
                Nothing to update
              </Badge>
            )}
            {backfill.isError && (
              <span className="text-destructive">
                Failed:{" "}
                {backfill.error.message || "An unexpected error occurred"}
              </span>
            )}
          </div>
          <Button
            size="sm"
            className="shrink-0"
            aria-label="Update Legacy acceptances"
            disabled={backfill.isPending}
            onClick={() => setIsConfirmOpen(true)}
          >
            {backfill.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {backfill.isPending ? "Updating…" : "Update"}
          </Button>
        </div>
      </CardContent>

      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title="Update legacy agreement acceptances?"
        description={`${DESCRIPTION} Acceptances already recorded are skipped. A commitment revoked since the last run is copied back, so run this once after the release, not after revoking.`}
        confirmText="Update"
        onConfirm={() => backfill.mutate()}
      />
    </Card>
  );
}

/**
 * The card's own instructions, one click away rather than a paragraph on every visit — the same
 * shape as the one on `UpdateResourceFieldsCard`.
 */
function HowThisWorks() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 min-h-8 shrink-0 gap-2 px-2 text-sm"
        >
          <CircleHelp className="h-4 w-4" />
          How this works
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[420px] text-sm">
        <h3 className="mb-2 font-semibold">How this works</h3>
        <ol className="list-decimal space-y-2 pl-4 text-muted-foreground">
          <li>
            Since 2026-10-19 the commitment check reads each person's own
            acceptances. Those given before that date sit only in the tenant's
            settings. <span className="text-foreground">Update</span> copies
            them to the tenant and to each person who accepted.
          </li>
          <li>
            Run it <span className="text-foreground">once</span>, right after
            the release, with both commitment flags turned off; turn them back
            on once it has finished.
          </li>
          <li>
            The result reads{" "}
            <span className="text-foreground">
              X tenant(s), Y user(s) updated of N
            </span>
            : N tenants still hold old acceptances, X of them and Y people got
            one. The old settings are kept as they are.
          </li>
          <li>
            Running it again copies nothing already recorded and says{" "}
            <span className="text-foreground">Nothing to update</span>. A run
            that fails keeps what it already copied; run it again.
          </li>
          <li>
            <span className="text-foreground">
              Do not run it after revoking
            </span>{" "}
            a commitment: the revoked acceptance is still in the old settings
            and would be copied back.
          </li>
        </ol>
      </PopoverContent>
    </Popover>
  );
}
